// Front-page Finnish copy — consumed VERBATIM from WP-N0's draft:
//   06-publication/drafts/front-page-fi.md  (Fable 5, v0.9, 2026-07-07)
// WP-E1 places this text; it does not rewrite it. Rich parts keep N0's
// emphasis (the minted-vocabulary terms) as markdown, rendered via <Prose>.
// Tile *copy* lives here; tile *icon + route* live in src/lib/site.ts (joined
// by slug), keeping Finnish content out of components (DESIGN-BRIEF §5).

export interface FrontTileCopy {
  slug: string
  title: string
  blurb: string
  /** Small (EN) tiles may carry a "(englanniksi)" marker, per N0 verbatim. */
  langNote?: string
}

export interface FrontFeaturedCopy {
  slug: string
  kicker: string
  title: string
  blurb: string
  cta: string
}

export interface FrontPageCopy {
  title: string
  subtitle: string
  /** Markdown — preserves the bold opening. */
  lead: string
  ctaPrimary: { label: string; href: string }
  ctaSecondary: { label: string; href: string }
  manifestoHeading: string
  /** Markdown — one paragraph, preserves N0's bold/italic vocabulary marks. */
  manifesto: string
  /** WP-S1: the featured band above the tiles (not a peer tile). */
  featured: FrontFeaturedCopy
  largeTiles: FrontTileCopy[]
  smallTiles: FrontTileCopy[]
}

export const frontPage: FrontPageCopy = {
  title: 'ArkkiKELA ja julkishallinnon kehittäminen tekoälyn aikakaudella',
  subtitle:
    'Ehdotus julkishallinnon tekoälykehityksen uudistamiseen — tapaus Kela ja muut.',
  lead: 'Kesällä 2026 julkisen sektorin tekoälykeskustelu keskittyy mahdollisiin säästöprosentteihin, ja konkretia keskustelun takana on vähäistä. Tämä sivusto hahmottaa konkreettisen vaihtoehdon: miten julkishallintoa voisi kehittää tekoälyn aikakaudella suoraan laista käsin. Kyse on koko julkishallinnosta, mutta tarkimmin katsotaan Kelaa: tutkimusmalli ArkkiKELA rakentaa Kansaneläkelaitoksen kokonaan uudestaan julkisista lähteistä. ArkkiValtio-osa näyttää, miten sama menetelmä laajenisi muuhun valtionhallintoon. Tavoitteena eivät ensisijaisesti ole säästöt, vaan yhteiskunnan palvelujen parantaminen ja elinvoiman kehittäminen.',
  ctaPrimary: { label: 'Aloita', href: '/kansalaiselle' },
  ctaSecondary: { label: 'Mikä tämä on?', href: '#manifesti' },
  manifestoHeading: 'Mikä ArkkiKELA on',
  manifesto:
    'ArkkiKELA on **tutkimus**: sen kautta tarkastellaan, miten julkishallintoa voisi kehittää tekoälyn aikakaudella. Tutkimusmalli on **arkkityyppinen synteettinen organisaatio** — Weberin ideaalityypin hengessä rakennettu kuvaus siitä, mitä Kelan on lain mukaan _olemuksellisesti_ tehtävä: etuudet, säännöt, tietosisällöt ja prosessit, ilman periytyneitä järjestelmiä ja historian kerrostumia.\n\nSe on **mittatikku, ei kilpailija**: se ei väitä, että todellinen Kela palvelisi tänään huonommin, vaan näyttää, mitä lainsäädännön tarkoitus sallii puhtaalta pöydältä rakennettuna.\n\nMallin rakensi tutkija **Kimmo Kontra** tekoälymallien kanssa osana Corporate Consilience -tutkimusta.\n\nTutkimus linkittyy syksyllä 2026 Yhdysvalloissa julkaistavaan kirjaan [_Corporate Stockholm Syndrome_](https://www.corporatestockholmsyndrome.com) ([Technics Publications](https://technicspub.com/)). [Kimmo Kontra](https://www.linkedin.com/in/kimmokontra/) on kirjoittanut sen [Janne Vihervuoren](https://www.linkedin.com/in/jannevihervuori/) kanssa.\n\nKaikki henkilöt ja tapaukset sivustolla ovat kuvitteellisia.',
  featured: {
    slug: '/synteettinen-suomi',
    kicker: 'Uutta',
    title: 'Synteettinen Suomi',
    blurb:
      '100 000 keksittyä suomalaista, rakennettu tietomallista. Avaa kymmenen heistä yhdellä napautuksella — tai etsi koko väestöstä suoraan selaimessa, ilman palvelinta. Ei yhtään oikeaa henkilöä.',
    cta: 'Tutustu synteettiseen väestöön',
  },
  largeTiles: [
    {
      slug: '/kansalaiselle',
      title: 'Kansalaiselle',
      blurb:
        '41 etuutta elämäntilanteesi mukaan. Katso, miltä etuuskatalogi näyttää, kun se johdetaan suoraan laista.',
    },
    {
      slug: '/tyontekijalle',
      title: 'ArkkiKELAlaiselle',
      blurb:
        'Kulje etuuskäsittely läpi vaihe vaiheelta: mikä on automaattista, missä ihminen päättää — ja miksi, pykälän tarkkuudella.',
    },
    {
      slug: '/lapileikkaus',
      title: 'Läpileikkaus',
      blurb:
        'Yksi perhe, yksi hakemus, koko matka: tapahtumat, tiedot, säännöt ja päätös yhdessä kuvassa.',
    },
    {
      slug: '/tarkoitus',
      title: 'Tarkoitus ja lainsäädäntö',
      blurb:
        'Miksi Kela on olemassa ja mitä laki siltä edellyttää — ja konepellin alla: koko määrittelygraafi, josta tämä sivusto on johdettu.',
    },
  ],
  smallTiles: [
    {
      slug: '/data-model',
      title: 'Tietomalli',
      blurb: 'Koko tietomalli yhtenä karttana — 42 tietokohdetta, selattavana.',
    },
    {
      slug: '/arkkivaltio',
      title: 'ArkkiValtio',
      blurb:
        'Entä jos sama tehtäisiin koko valtiolle? Neljä virastoa, kaksi rekisterirataa ja kolme elämäntapahtumaa — kuvitustason jatke tutkimusmallille.',
    },
  ],
}
